import type { AppointmentView, CalendarResponse } from "@vaulted/shared";
import { HOLD_BACKGROUND, HoldBadge } from "../components/HoldBadge";
import { STATUS_LABELS, isOpenStatus } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type DragEvent, type MouseEvent, type ReactNode } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { errorMessage, get, post } from "../api";
import { GfeBadge } from "../components/Gfe";
import { FormsBadge, STATUS_TONE, time12 } from "../components/AppointmentDialog";
import { BookingDialog, type BookingPrefill } from "../components/BookingDialog";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Loading, PageHeader, cx } from "../components/ui";
import { displayName } from "./Clients";

const PX_PER_MIN = 1.2;
const SNAP = 15;

/** Today's date (YYYY-MM-DD) in a time zone; the browser's zone until the clinic's is known. */
export function todayIn(tz?: string) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}
/** Minutes after midnight right now in a time zone. */
export function nowMinutesIn(tz?: string) {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date());
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return n("hour") * 60 + n("minute");
}
export function addDays(date: string, n: number) {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
const weekStart = (date: string) => addDays(date, -((new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7));
const minutesOf = (local: string) => Number(local.slice(11, 13)) * 60 + Number(local.slice(14, 16));
const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
/**
 * The date bar under a page's title (Calendar, Front desk): ‹ Today ›, the date box, the date in words,
 * and the page's own controls on the right.
 */
export function DateBar({
  date,
  label,
  onPrev,
  onToday,
  onNext,
  onPick,
  min,
  children,
}: {
  date: string;
  /** Earliest date it can go to (e.g. today): going back stops there. */
  min?: string;
  label: string;
  onPrev: () => void;
  onToday: () => void;
  onNext: () => void;
  onPick: (date: string) => void;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex">
        <Button variant="quiet" onClick={onPrev} aria-label="Previous" disabled={!!min && date <= min}>
          ‹
        </Button>
        <Button variant="quiet" onClick={onToday}>
          Today
        </Button>
        <Button variant="quiet" onClick={onNext} aria-label="Next">
          ›
        </Button>
      </div>
      <input type="date" value={date} min={min} onChange={(e) => e.target.value && onPick(e.target.value)} className="rounded-[var(--radius-control)] border border-line bg-white px-2.5 py-1.5 text-sm" aria-label="Date" />
      <h2 className="text-lg font-semibold">{label}</h2>
      {children && <div className="ml-auto flex flex-wrap items-center gap-3">{children}</div>}
    </div>
  );
}

export const dayLabel = (date: string, opts: Intl.DateTimeFormatOptions = { weekday: "long", month: "short", day: "numeric" }) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, { ...opts, timeZone: "UTC" });
const minLabel = (m: number) => time12(`0000-00-00T${hhmm(m)}`);
const hourLabel = (m: number) => `${((Math.floor(m / 60) + 11) % 12) + 1} ${m < 720 ? "AM" : "PM"}`;

export function useCalendar(from: string, to: string, enabled = true, refreshMs = 60_000) {
  return useQuery({ queryKey: ["calendar", from, to], queryFn: () => get<CalendarResponse>(`/api/calendar?from=${from}&to=${to}`), refetchInterval: refreshMs, enabled });
}

/** Side-by-side lanes for overlapping appointments within one column. */
function lanes(items: { a: AppointmentView; start: number; end: number }[]) {
  const sorted = [...items].sort((x, y) => x.start - y.start || y.end - x.end);
  const laneEnds: number[] = [];
  const placed = sorted.map((it) => {
    let lane = laneEnds.findIndex((end) => end <= it.start);
    if (lane < 0) lane = laneEnds.push(0) - 1;
    laneEnds[lane] = it.end;
    return { ...it, lane };
  });
  // Width is shared within each cluster of overlapping items.
  const out: (typeof placed[number] & { lanes: number })[] = [];
  let cluster: typeof placed = [];
  let clusterEnd = -1;
  const flush = () => {
    const n = Math.max(1, ...cluster.map((c) => c.lane + 1));
    out.push(...cluster.map((c) => ({ ...c, lanes: n })));
    cluster = [];
  };
  for (const p of placed) {
    if (cluster.length && p.start >= clusterEnd) flush();
    cluster.push(p);
    clusterEnd = Math.max(clusterEnd, p.end);
  }
  flush();
  return out;
}

interface Column {
  key: string;
  label: string;
  sub?: string;
  staffId: string | null;
  /** Shaded as open; everything else is grey. */
  open: { start: number; end: number }[];
  items: { a: AppointmentView; start: number; end: number; phaseEnd: number; visit?: [number, number] }[];
  blocks: { start: number; end: number; reason: string }[];
}

/** The calendar's last view on this device (not shared; storage can be unavailable, e.g. private browsing). */
function remembered(k: "view" | "by"): string | null {
  try {
    return localStorage.getItem(`calendar.${k}`);
  } catch {
    return null;
  }
}
function remember(k: "view" | "by", v: string) {
  try {
    localStorage.setItem(`calendar.${k}`, v);
  } catch {
    /* not saved: fine */
  }
}

export function CalendarPage() {
  const [params, setParams] = useSearchParams();
  // Day or week, staff or chairs: the address wins, else what this device used last.
  const view = (params.get("view") ?? remembered("view")) === "week" ? "week" : "day";
  const by = (params.get("by") ?? remembered("by")) === "chairs" ? "chairs" : "staff";
  const [date, setDateState] = useState(params.get("date") ?? todayIn());
  const [showCancelled, setShowCancelled] = useState(false);
  const [booking, setBooking] = useState<BookingPrefill | null>(null);
  const openVisit = useNavigate();
  const setOpenId = (id: string) => openVisit(`/visits/${id}`);
  const [pendingMove, setPendingMove] = useState<{ a: AppointmentView; start: string; staffId: string | null; message: string } | null>(null);
  const [forceReason, setForceReason] = useState("");

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    next.set(k, v);
    setParams(next, { replace: true });
    if (k === "view" || k === "by") remember(k, v);
  };
  const setDate = (d: string) => {
    setDateState(d);
    setParam("date", d);
  };

  const from = view === "week" ? weekStart(date) : date;
  const to = view === "week" ? addDays(from, 6) : date;
  const cal = useCalendar(from, to);
  const qc = useQueryClient();
  const tz = cal.data?.timezone;
  const staffName = (id: string | null) => cal.data?.staff.find((s) => s.id === id)?.name ?? "";

  const move = useMutation({
    mutationFn: (v: { a: AppointmentView; start: string; staffId: string | null; forceReason?: string }) =>
      post(`/api/appointments/${v.a.id}/move`, { start: v.start, staffId: v.staffId, forceReason: v.forceReason || null }),
    onSuccess: async () => {
      setPendingMove(null);
      setForceReason("");
      await qc.invalidateQueries({ queryKey: ["calendar"] });
    },
    onError: (err, v) => {
      if (!v.forceReason) setPendingMove({ a: v.a, start: v.start, staffId: v.staffId, message: errorMessage(err) });
    },
  });

  const shift = (n: number) => setDate(addDays(date, view === "week" ? n * 7 : n));
  const visible = (a: AppointmentView) => showCancelled || isOpenStatus(a.status);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Calendar"
        action={
          <Button variant="primary" onClick={() => setBooking({ date })}>
            New booking
          </Button>
        }
      />
      <DateBar
        date={date}
        label={view === "week" ? `Week of ${dayLabel(from, { month: "short", day: "numeric" })}` : dayLabel(date)}
        onPrev={() => shift(-1)}
        onToday={() => setDate(todayIn(tz))}
        onNext={() => shift(1)}
        onPick={setDate}
      >
        <Toggle value={view} options={[["day", "Day"], ["week", "Week"]]} onChange={(v) => setParam("view", v)} />
        {view === "day" && <Toggle value={by} options={[["staff", "Staff"], ["chairs", "Equipment"]]} onChange={(v) => setParam("by", v)} />}
        <Checkbox label="Show cancelled" checked={showCancelled} onChange={setShowCancelled} />
      </DateBar>
      {move.error && !pendingMove && <Alert>{errorMessage(move.error)}</Alert>}
      {cal.error && <Alert>{errorMessage(cal.error)}</Alert>}
      {!cal.data ? (
        !cal.error && <Loading />
      ) : view === "week" ? (
        <WeekView data={cal.data} from={from} visible={visible} staffName={staffName} onOpen={setOpenId} onDay={(d) => (setDate(d), setParam("view", "day"))} />
      ) : (
        <DayView
          data={cal.data}
          date={date}
          by={by}
          visible={visible}
          staffName={staffName}
          onOpen={setOpenId}
          onBook={(time, staffId) => setBooking({ date, time, staffId: staffId ?? undefined })}
          onMove={(a, start, staffId) => move.mutate({ a, start, staffId })}
        />
      )}

      {booking && cal.data && <BookingDialog prefill={booking} staff={cal.data.staff} onClose={() => setBooking(null)} onBooked={() => setBooking(null)} />}
      {pendingMove && (
        <Dialog
          title="Can't move it there"
          onClose={() => setPendingMove(null)}
          footer={
            <>
              <Button variant="quiet" onClick={() => setPendingMove(null)}>
                Leave it
              </Button>
              <Button
                variant="danger"
                disabled={!forceReason.trim()}
                busy={move.isPending}
                onClick={() => move.mutate({ a: pendingMove.a, start: pendingMove.start, staffId: pendingMove.staffId, forceReason })}
              >
                Move anyway
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <p className="text-sm">
              {displayName(pendingMove.a.client)} to {time12(pendingMove.start)}
              {pendingMove.staffId && ` with ${staffName(pendingMove.staffId)}`}: {pendingMove.message}
            </p>
            <Field label="Reason to move it anyway" hint="Saved in the audit log." value={forceReason} onChange={(e) => setForceReason(e.target.value)} />
            {move.error && pendingMove && move.variables?.forceReason && <Alert>{errorMessage(move.error)}</Alert>}
          </div>
        </Dialog>
      )}
    </div>
  );
}

function Toggle<T extends string>({ value, options, onChange }: { value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="flex rounded-[var(--radius-control)] border border-line bg-white p-0.5 text-sm">
      {options.map(([v, label]) => (
        <button key={v} onClick={() => onChange(v)} className={cx("rounded-[calc(var(--radius-control)-2px)] px-3 py-1 font-semibold", v === value ? "bg-brand text-white" : "text-ink-soft hover:bg-mist")}>
          {label}
        </button>
      ))}
    </div>
  );
}

function DayView({
  data,
  date,
  by,
  visible,
  staffName,
  onOpen,
  onBook,
  onMove,
}: {
  data: CalendarResponse;
  date: string;
  by: "staff" | "chairs";
  visible: (a: AppointmentView) => boolean;
  staffName: (id: string | null) => string;
  onOpen: (id: string) => void;
  onBook: (time: string, staffId: string | null) => void;
  onMove: (a: AppointmentView, start: string, staffId: string | null) => void;
}) {
  const appts = data.appointments.filter((a) => a.start.slice(0, 10) === date && visible(a));
  const openHours = data.openHours.find((o) => o.date === date)?.periods ?? [];

  const columns = useMemo<Column[]>(() => {
    if (by === "chairs") {
      const perType = new Map<string, number>();
      for (const u of data.units) perType.set(u.typeId, (perType.get(u.typeId) ?? 0) + 1);
      const units = [...data.units].sort((a, b) => perType.get(b.typeId)! - perType.get(a.typeId)! || a.typeName.localeCompare(b.typeName) || a.name.localeCompare(b.name, undefined, { numeric: true }));
      return units.map((u) => ({
        key: u.id,
        label: u.name,
        sub: u.typeName,
        staffId: null,
        open: openHours,
        items: appts.filter((a) => a.resourceIds.includes(u.id)).map((a) => ({ a, start: minutesOf(a.start), end: Math.max(minutesOf(a.resourcesFree), minutesOf(a.start) + SNAP), phaseEnd: minutesOf(a.end) })),
        blocks: data.blocks
          .filter((b) => (b.resourceId === u.id || (!b.resourceId && b.typeId === u.typeId)) && b.start.slice(0, 10) <= date && b.end.slice(0, 10) >= date)
          .map((b) => ({ start: b.start.slice(0, 10) < date ? 0 : minutesOf(b.start), end: b.end.slice(0, 10) > date ? 1440 : minutesOf(b.end), reason: b.reason })),
      }));
    }
    const avail = data.availability.filter((x) => x.date === date);
    const shifts = (data.eventShifts ?? []).filter((x) => x.start.slice(0, 10) <= date && x.end.slice(0, 10) >= date);
    const working = new Set([...avail.filter((x) => x.periods.length).map((x) => x.staffId), ...appts.map((a) => a.staffId), ...shifts.map((x) => x.staffId)]);
    // Mobile visits keep the nurse for travel before and after.
    const staffItem = (a: AppointmentView) =>
      a.context === "mobile"
        ? { a, start: minutesOf(a.staffFrom), end: Math.max(minutesOf(a.staffEnd), minutesOf(a.end)), phaseEnd: minutesOf(a.end), visit: [minutesOf(a.start), minutesOf(a.end)] as [number, number] }
        : { a, start: minutesOf(a.start), end: Math.max(minutesOf(a.end), minutesOf(a.start) + SNAP), phaseEnd: minutesOf(a.staffEnd) };
    const staff = data.staff.filter((s) => working.has(s.id));
    const cols: Column[] = (staff.length ? staff : data.staff).map((s) => {
      const periods = avail.find((x) => x.staffId === s.id)?.periods ?? [];
      return {
        key: s.id,
        label: s.name,
        sub: periods.length ? periods.map((p) => `${minLabel(p.start)}–${minLabel(p.end)}`).join(", ") : shifts.some((x) => x.staffId === s.id) ? "At an event" : "Off",
        staffId: s.id,
        open: periods,
        items: appts.filter((a) => a.staffId === s.id).map(staffItem),
        blocks: shifts
          .filter((x) => x.staffId === s.id)
          .map((x) => ({ start: x.start.slice(0, 10) < date ? 0 : minutesOf(x.start), end: x.end.slice(0, 10) > date ? 1440 : minutesOf(x.end), reason: `At ${x.eventName}` })),
      };
    });
    const unassigned = appts.filter((a) => !a.staffId);
    if (unassigned.length) {
      cols.push({ key: "none", label: "No nurse", staffId: null, open: openHours, items: unassigned.map(staffItem), blocks: [] });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, date, by, appts.length]);

  // Visible range: posted hours and shifts, widened to fit bookings; 8 AM–6 PM if nothing is set.
  const edges = [...openHours.flatMap((p) => [p.start, p.end]), ...columns.flatMap((c) => [...c.open.flatMap((p) => [p.start, p.end]), ...c.items.flatMap((i) => [i.start, i.end])])];
  const dayStart = Math.max(0, Math.floor((edges.length ? Math.min(...edges) : 480) / 60) * 60 - 60);
  const dayEnd = Math.min(1440, Math.ceil((edges.length ? Math.max(...edges) : 1080) / 60) * 60 + 60);
  const height = (dayEnd - dayStart) * PX_PER_MIN;
  const y = (m: number) => (m - dayStart) * PX_PER_MIN;
  const isToday = date === todayIn(data.timezone);
  const now = nowMinutesIn(data.timezone);

  const [drag, setDrag] = useState<{ id: string; offsetMin: number } | null>(null);
  const minuteAt = (e: { clientY: number; currentTarget: HTMLElement }, offset = 0) => {
    const top = e.currentTarget.getBoundingClientRect().top;
    const m = dayStart + (e.clientY - top) / PX_PER_MIN - offset;
    return Math.max(dayStart, Math.min(dayEnd - SNAP, Math.round(m / SNAP) * SNAP));
  };

  if (!columns.length) {
    return <Alert tone="info">{by === "chairs" ? "No equipment set up yet. Add chairs under Settings → Resources." : "No staff yet."}</Alert>;
  }

  return (
    <div className="overflow-x-auto rounded-[var(--radius-panel)] border border-line bg-white">
      <div className="grid min-w-fit" style={{ gridTemplateColumns: `3.5rem repeat(${columns.length}, minmax(9rem, 1fr))` }}>
        <div className="sticky left-0 z-20 border-b border-line bg-white" />
        {columns.map((c) => (
          <div key={c.key} className="border-b border-l border-line px-2 py-2">
            <div className="truncate text-sm font-semibold">{c.label}</div>
            {c.sub && <div className="truncate text-xs text-muted">{c.sub}</div>}
          </div>
        ))}
        <div className="sticky left-0 z-20 bg-white" style={{ height }}>
          {Array.from({ length: (dayEnd - dayStart) / 60 }, (_, i) => dayStart + i * 60).map((m) => (
            <div key={m} className="absolute right-1.5 -translate-y-1/2 text-[11px] text-muted" style={{ top: y(m) }}>
              {m > dayStart && hourLabel(m)}
            </div>
          ))}
        </div>
        {columns.map((c) => (
          <div
            key={c.key}
            className="relative cursor-cell border-l border-line bg-mist/70"
            style={{ height }}
            onClick={(e: MouseEvent<HTMLDivElement>) => {
              if (e.target !== e.currentTarget && !(e.target as HTMLElement).dataset.slot) return;
              onBook(hhmm(Math.floor((dayStart + (e.clientY - e.currentTarget.getBoundingClientRect().top) / PX_PER_MIN) / SNAP) * SNAP), c.staffId);
            }}
            onDragOver={(e: DragEvent<HTMLDivElement>) => drag && e.preventDefault()}
            onDrop={(e: DragEvent<HTMLDivElement>) => {
              e.preventDefault();
              if (!drag) return;
              const a = data.appointments.find((x) => x.id === drag.id);
              setDrag(null);
              if (!a) return;
              const start = `${date}T${hhmm(minuteAt(e, drag.offsetMin))}`;
              const staffId = by === "staff" ? c.staffId : a.staffId;
              if (start === a.start && staffId === a.staffId) return;
              onMove(a, start, by === "staff" ? staffId : null);
            }}
          >
            {c.open.map((p, i) => (
              <div key={i} data-slot="1" className="absolute inset-x-0 bg-white" style={{ top: y(Math.max(p.start, dayStart)), height: (Math.min(p.end, dayEnd) - Math.max(p.start, dayStart)) * PX_PER_MIN }} />
            ))}
            {Array.from({ length: (dayEnd - dayStart) / 60 }, (_, i) => dayStart + i * 60).map((m) => (
              <div key={m} className="pointer-events-none absolute inset-x-0 border-t border-line/70" style={{ top: y(m) }} />
            ))}
            {c.blocks.map((b, i) => (
              <div
                key={i}
                title={b.reason}
                className="pointer-events-none absolute inset-x-0 overflow-hidden px-1.5 py-0.5 text-[11px] text-muted"
                style={{ top: y(Math.max(b.start, dayStart)), height: (Math.min(b.end, dayEnd) - Math.max(b.start, dayStart)) * PX_PER_MIN, background: "repeating-linear-gradient(45deg, var(--color-mist), var(--color-mist) 6px, var(--color-line) 6px, var(--color-line) 8px)" }}
              >
                {b.reason}
              </div>
            ))}
            {lanes(c.items).map(({ a, start, end, lane, lanes: n, ...rest }) => {
              const { phaseEnd, visit } = rest as { phaseEnd: number; visit?: [number, number] };
              const ended = !isOpenStatus(a.status);
              const color = a.color ?? "var(--color-brand)";
              const pct = (m: number) => `${((m - start) / Math.max(end - start, 1)) * 100}%`;
              const strong = `color-mix(in srgb, ${color} 28%, white)`;
              const light = `color-mix(in srgb, ${color} 9%, white)`;
              // Mobile: light travel time around the visit. Clinic: the nurse's start time darker.
              const background = visit
                ? `linear-gradient(to bottom, ${light} ${pct(visit[0])}, ${strong} ${pct(visit[0])} ${pct(visit[1])}, ${light} ${pct(visit[1])})`
                : `linear-gradient(to bottom, ${strong} ${pct(phaseEnd)}, ${light} 0)`;
              return (
                <button
                  key={a.id}
                  draggable={!ended && a.status !== "completed" && a.context === "clinic"}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    const top = (e.currentTarget as HTMLElement).getBoundingClientRect().top;
                    setDrag({ id: a.id, offsetMin: Math.round((e.clientY - top) / PX_PER_MIN) });
                  }}
                  onDragEnd={() => setDrag(null)}
                  onClick={() => onOpen(a.id)}
                  className={cx("absolute overflow-hidden rounded-md border text-left text-xs shadow-sm transition hover:z-10 hover:shadow-md", ended && "opacity-50", (a.bookingStatus === "requested" || a.holdUntil) && "border-dashed")}
                  style={{
                    top: y(start),
                    height: Math.max((end - start) * PX_PER_MIN - 2, 18),
                    left: `calc(${(lane / n) * 100}% + 2px)`,
                    width: `calc(${100 / n}% - 4px)`,
                    borderColor: a.holdUntil ? "#d97706" : color,
                    background: a.holdUntil ? HOLD_BACKGROUND : background,
                  }}
                  title={`${displayName(a.client)} · ${a.serviceName} · ${time12(a.start)}–${time12(a.end)} · ${STATUS_LABELS[a.status]}`}
                >
                  <div className="px-1.5 py-0.5">
                    <div className="flex items-center gap-1 font-semibold">
                      {a.client.schedulingAlert && <span title={a.client.schedulingAlert}>⚠</span>}
                      {a.formsNeeded.length > 0 && a.status !== "cancelled" && (end - start) * PX_PER_MIN <= 60 && <span className="text-wait" title={`Forms needed: ${a.formsNeeded.map((f) => f.name).join(", ")}`}>✎</span>}
                      <span className={cx("truncate", a.status === "cancelled" && "line-through")}>{displayName(a.client)}</span>
                    </div>
                    <div className="truncate text-ink-soft">
                      {time12(a.start)} · {a.serviceName}
                    </div>
                    {a.context === "mobile" && <div className="truncate text-muted">{a.bookingStatus === "requested" ? "Requested · " : "Mobile · "}{a.address?.split(",").slice(-2, -1)[0]?.trim()}</div>}
                    {a.context === "event" && <div className="truncate text-muted">{a.eventName}</div>}
                    {a.holdUntil && <HoldBadge until={a.holdUntil} compact={(end - start) * PX_PER_MIN <= 60} />}
                    {by === "chairs" && a.staffId && <div className="truncate text-muted">{staffName(a.staffId)}</div>}
                    {(end - start) * PX_PER_MIN > 60 && (
                      <div className="mt-0.5 flex flex-wrap items-center gap-1">
                        {!a.holdUntil && <Badge tone={STATUS_TONE[a.status]}>{STATUS_LABELS[a.status]}</Badge>}
                        <FormsBadge a={a} compact />
                        {a.status !== "cancelled" && a.status !== "completed" && <GfeBadge g={a.gfe} compact />}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
            {isToday && now >= dayStart && now <= dayEnd && <div className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-danger" style={{ top: y(now) }} />}
          </div>
        ))}
      </div>
      <p className="border-t border-line px-3 py-2 text-xs text-muted">
        Click an open spot to book. Drag a clinic booking to move it. The darker part is when the {by === "staff" ? "nurse is busy" : "service runs"}; the lighter part is {by === "staff" ? "drip time (mobile: travel)" : "cleanup"}. Dashed: a mobile request waiting for a nurse. Grey is outside {by === "staff" ? "the shift" : "posted hours"}; striped is away at an event.
      </p>
    </div>
  );
}

function WeekView({
  data,
  from,
  visible,
  staffName,
  onOpen,
  onDay,
}: {
  data: CalendarResponse;
  from: string;
  visible: (a: AppointmentView) => boolean;
  staffName: (id: string | null) => string;
  onOpen: (id: string) => void;
  onDay: (date: string) => void;
}) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(from, i));
  const today = todayIn(data.timezone);
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-7">
      {days.map((d) => {
        const list = data.appointments.filter((a) => a.start.slice(0, 10) === d && visible(a)).sort((x, y) => x.start.localeCompare(y.start));
        const onShift = data.availability.filter((x) => x.date === d && x.periods.length).length;
        return (
          <section key={d} className={cx("flex min-h-32 flex-col rounded-[var(--radius-panel)] border bg-white", d === today ? "border-brand" : "border-line")}>
            <button onClick={() => onDay(d)} className="border-b border-line px-3 py-2 text-left hover:bg-mist">
              <div className="text-sm font-semibold">{dayLabel(d, { weekday: "short", month: "short", day: "numeric" })}</div>
              <div className="text-xs text-muted">
                {list.length} booked · {onShift} on shift
              </div>
            </button>
            <ul className="flex flex-col gap-1 p-1.5">
              {list.map((a) => (
                <li key={a.id}>
                  <button onClick={() => onOpen(a.id)} className={cx("w-full rounded-md border-l-4 bg-mist/60 px-2 py-1 text-left text-xs hover:bg-mist", !isOpenStatus(a.status) && "opacity-50")} style={{ borderColor: a.color ?? "var(--color-brand)" }}>
                    <div className="font-semibold">
                      {time12(a.start)} {displayName(a.client)}
                    </div>
                    <div className="truncate text-ink-soft">
                      {a.serviceName}
                      {a.staffId && ` · ${staffName(a.staffId)}`}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
